import React from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import {
  Panel,
  PanelEmpty,
  CHART_BASE,
  CHART_GRID_NO_X
} from '../../../../component/common/designSystem';
import { BAND_RAMP } from './moodTheme';

/**
 * One context category — what people were doing, who they were with, or where
 * they were — as a 100% stacked bar per option.
 *
 * Proportions rather than counts is the whole point: "Work" always has the most
 * check-ins simply because people are at work most, so a raw bar chart just
 * re-draws the shape of the working week. Normalising each row to its own total
 * is what surfaces the actual finding — that the difficult share at work is
 * double the one at home.
 *
 * The volume is still shown, as the count next to each row's tooltip, because a
 * context with four check-ins behind it is not a finding either.
 */
const ContextMixPanel = ({ title, subtitle, rows, emptyText, height }) => {
  // Apex draws horizontal categories bottom-up, so the most-logged context is
  // reversed into the top slot.
  const ordered = [...rows].reverse();
  const bandLabels = rows[0]?.bands?.map((band) => band.label) || [];

  const series = bandLabels.map((label, index) => ({
    name: label,
    data: ordered.map((row) => row.bands?.[index]?.count || 0)
  }));

  const options = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true, stackType: '100%' },
    colors: BAND_RAMP,
    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '65%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID_NO_X,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: {
      categories: ordered.map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val) => `${val.toLocaleString()} check-ins` }
    }
  };

  return (
    <Panel title={title} subtitle={subtitle}>
      {rows.length > 0 ? (
        <Chart type="bar" height={height} width="100%" series={series} options={options} />
      ) : (
        <PanelEmpty>{emptyText}</PanelEmpty>
      )}
    </Panel>
  );
};

ContextMixPanel.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  rows: PropTypes.array,
  emptyText: PropTypes.string,
  height: PropTypes.number
};

ContextMixPanel.defaultProps = {
  rows: [],
  emptyText: 'No check-ins recorded yet.',
  height: 340
};

export default ContextMixPanel;
